import PICKUPS from "../consts/PICKUPS";
import WEAPONS from "../consts/WEAPONS";
import PickupType from "../enums/PickupType";
import WeaponType from "../enums/WeaponType";
import { Sizer } from "phaser3-rex-plugins/templates/ui/ui-components";
import RexUIPlugin from "phaser3-rex-plugins/templates/ui/ui-plugin.js";
import { TEXT_STYLES } from "../../utils/FontManager";
import { t } from "../../utils/i18n";
import { SPRITESHEET_KEYS, SPRITESHEET_FRAMES } from "../consts/SPRITESHEETS";

export default class LevelUpItemPanel extends Sizer {
  weaponType: WeaponType | PickupType;
  rexUI: RexUIPlugin;
  Icon: any;
  Name: any;
  Description: any;
  NextLevel: any;
  private scaleRatio: number;

  // Helper function to get atlas texture info from icon name
  private getAtlasTexture(
    iconName: string
  ): { key: string; frame: string } | null {
    // Map icon names to their atlas locations
    const mapping: Record<string, { key: string; frame: string }> = {
      // Pickups
      gem_blue: {
        key: SPRITESHEET_KEYS.PICKUPS,
        frame: SPRITESHEET_FRAMES.PICKUPS.GEM_BLUE,
      },
      gem_purple: {
        key: SPRITESHEET_KEYS.PICKUPS,
        frame: SPRITESHEET_FRAMES.PICKUPS.GEM_PURPLE,
      },
      gem_gold: {
        key: SPRITESHEET_KEYS.PICKUPS,
        frame: SPRITESHEET_FRAMES.PICKUPS.GEM_GOLD,
      },
      coin: {
        key: SPRITESHEET_KEYS.PICKUPS,
        frame: SPRITESHEET_FRAMES.PICKUPS.COIN,
      },
      magnet_pickup: {
        key: SPRITESHEET_KEYS.PICKUPS,
        frame: SPRITESHEET_FRAMES.PICKUPS.MAGNET_PICKUP,
      },
      apple: {
        key: SPRITESHEET_KEYS.PICKUPS,
        frame: SPRITESHEET_FRAMES.PICKUPS.APPLE,
      },
      treasure_chest: {
        key: SPRITESHEET_KEYS.PICKUPS,
        frame: SPRITESHEET_FRAMES.PICKUPS.TREASURE_CHEST,
      },
      camel_chest: {
        key: SPRITESHEET_KEYS.PICKUPS,
        frame: SPRITESHEET_FRAMES.PICKUPS.CAMEL_CHEST,
      },
      gift: {
        key: SPRITESHEET_KEYS.PICKUPS,
        frame: SPRITESHEET_FRAMES.PICKUPS.GIFT,
      },
      skull: {
        key: SPRITESHEET_KEYS.PICKUPS,
        frame: SPRITESHEET_FRAMES.PICKUPS.SKULL,
      },
      star: {
        key: SPRITESHEET_KEYS.PICKUPS,
        frame: SPRITESHEET_FRAMES.PICKUPS.STAR,
      },

      // Stats
      magnet: {
        key: SPRITESHEET_KEYS.STATS,
        frame: SPRITESHEET_FRAMES.STATS.MAGNET,
      },
      power: {
        key: SPRITESHEET_KEYS.STATS,
        frame: SPRITESHEET_FRAMES.STATS.POWER,
      },
      range: {
        key: SPRITESHEET_KEYS.STATS,
        frame: SPRITESHEET_FRAMES.STATS.RANGE,
      },
      cooldown: {
        key: SPRITESHEET_KEYS.STATS,
        frame: SPRITESHEET_FRAMES.STATS.COOLDOWN,
      },
      speed: {
        key: SPRITESHEET_KEYS.STATS,
        frame: SPRITESHEET_FRAMES.STATS.SPEED,
      },
      duration: {
        key: SPRITESHEET_KEYS.STATS,
        frame: SPRITESHEET_FRAMES.STATS.DURATION,
      },
      armor: {
        key: SPRITESHEET_KEYS.STATS,
        frame: SPRITESHEET_FRAMES.STATS.ARMOR,
      },
      maxhealth: {
        key: SPRITESHEET_KEYS.STATS,
        frame: SPRITESHEET_FRAMES.STATS.MAXHEALTH,
      },
      growth: {
        key: SPRITESHEET_KEYS.STATS,
        frame: SPRITESHEET_FRAMES.STATS.GROWTH,
      },
      movespeed: {
        key: SPRITESHEET_KEYS.STATS,
        frame: SPRITESHEET_FRAMES.STATS.MOVESPEED,
      },
      luck: {
        key: SPRITESHEET_KEYS.STATS,
        frame: SPRITESHEET_FRAMES.STATS.LUCK,
      },

      // Weapons
      hashblade_icon: {
        key: SPRITESHEET_KEYS.WEAPONS,
        frame: SPRITESHEET_FRAMES.WEAPONS.HASHBLADE_ICON,
      },
      blast_icon: {
        key: SPRITESHEET_KEYS.WEAPONS,
        frame: SPRITESHEET_FRAMES.WEAPONS.BLAST_ICON,
      },
      boomerang: {
        key: SPRITESHEET_KEYS.WEAPONS,
        frame: SPRITESHEET_FRAMES.WEAPONS.BOOMERANG,
      },
      rock: {
        key: SPRITESHEET_KEYS.WEAPONS,
        frame: SPRITESHEET_FRAMES.WEAPONS.ROCK,
      },
      bubble: {
        key: SPRITESHEET_KEYS.WEAPONS,
        frame: SPRITESHEET_FRAMES.WEAPONS.BUBBLE,
      },
      fork: {
        key: SPRITESHEET_KEYS.WEAPONS,
        frame: SPRITESHEET_FRAMES.WEAPONS.FORK,
      },
      arrow: {
        key: SPRITESHEET_KEYS.WEAPONS,
        frame: SPRITESHEET_FRAMES.WEAPONS.ARROW,
      },
      stoneaura_icon: {
        key: SPRITESHEET_KEYS.WEAPONS,
        frame: SPRITESHEET_FRAMES.WEAPONS.STONEAURA_ICON,
      },
    };

    return mapping[iconName] || null;
  }

  constructor(
    scene: Phaser.Scene,
    width: number,
    height: number,
    scaleRatio: number
  ) {
    super(scene, {
      width: width,
      height: height,
      orientation: 0,
      space: {
        left: 15 * scaleRatio,
        right: 15 * scaleRatio,
        top: 12 * scaleRatio,
        bottom: 12 * scaleRatio,
        item: 10 * scaleRatio,
      },
    });
    this.scaleRatio = scaleRatio;
    this.rexUI = scene.rexUI;
    this.addBackground(
      this.rexUI.add.roundRectangle(0, 0, 0, 0, 10 * scaleRatio, 0x293648)
    );

    const iconSize = 32 * scaleRatio;
    const icon = this.scene.add
      .image(0, 0, "main")
      .setDisplaySize(iconSize, iconSize);

    const textContainer = this.rexUI.add.sizer({
      orientation: 1,
      space: { item: 5 * scaleRatio },
    });

    const name = this.scene.add
      .text(0, 0, "Name", {
        ...TEXT_STYLES.REGULAR,
        fontSize: `${18 * this.scaleRatio}px`,
        color: "#FFFFFF",
        wordWrap: {
          width: width - 90 * this.scaleRatio,
          useAdvancedWrap: true,
        },
      })
      .setOrigin(0, 0.5);

    const description = this.scene.add
      .text(0, 0, "Description", {
        ...TEXT_STYLES.REGULAR,
        fontSize: `${15 * this.scaleRatio}px`,
        color: "#84919E",
        lineSpacing: 4 * this.scaleRatio,
        wordWrap: {
          width: width - 90 * this.scaleRatio,
          useAdvancedWrap: true,
        },
      })
      .setOrigin(0, 0.5);

    textContainer.add(name, { align: "left" });
    textContainer.add(description, { align: "left" });

    const level = this.scene.add
      .text(0, 0, "New!", {
        ...TEXT_STYLES.REGULAR,
        fontSize: `${10 * this.scaleRatio}px`,
        color: "#FFFFFF",
      })
      .setOrigin(1, 0.5);

    this.add(icon);
    this.add(textContainer, { proportion: 1, align: "left" });
    this.add(level, { align: "right-center" });

    this.Icon = icon;
    this.Name = name;
    this.NextLevel = level;
    this.Description = description;
  }
  AssignData(weaponType: WeaponType, level: number = 0) {
    this.weaponType = weaponType;
    const weaponData = WEAPONS[weaponType][0];

    // Use translation keys for name
    const nameKey =
      weaponData.nameKey || `weapons.${String(weaponType).toLowerCase()}.name`;
    this.Name.setText(t(nameKey));

    if (!!weaponData.iconName) {
      const atlasInfo = this.getAtlasTexture(weaponData.iconName);
      if (atlasInfo) {
        this.Icon.setTexture(atlasInfo.key, atlasInfo.frame);
      } else {
        this.Icon.setTexture(weaponData.iconName); // Fallback for non-spritesheet icons
      }
    } else {
      this.Icon.setFrame(weaponData.frameName);
    }

    this.Icon.setDisplaySize(32 * this.scaleRatio, 32 * this.scaleRatio);
    if (level > 0) {
      const nextLevelData = WEAPONS[weaponType][level];
      this.NextLevel.setText(`Lvl ${level + 1}`);
      this.NextLevel.setColor("#FFFFFF");
      this.Description.setText(
        LevelUpItemPanel.ParseLevelUpInfo(nextLevelData, weaponData.isPowerUp)
      );
    } else {
      this.NextLevel.setText("New!");
      this.NextLevel.setColor("#FFFFFF");
      // Use translation keys for description
      const descriptionKey =
        weaponData.descriptionKey ||
        `weapons.${String(weaponType).toLowerCase()}.description`;
      this.Description.setText(t(descriptionKey));
    }
  }
  static ParseLevelUpInfo(
    nextLevelData: any,
    isPowerUp: any
  ): string | string[] {
    let temp = "";
    for (const key in nextLevelData) {
      const v = nextLevelData[key];
      switch (key) {
        case "power":
          temp += `${t(
            isPowerUp
              ? "weapons.upgrades.power.powerUp"
              : "weapons.upgrades.power.normal",
            {
              value: isPowerUp ? (v * 100).toFixed(0) : (v * 10).toFixed(0),
            }
          )}\n`;
          break;
        case "area":
          temp += `${t(
            isPowerUp
              ? "weapons.upgrades.area.powerUp"
              : "weapons.upgrades.area.normal",
            {
              value: (v * 100).toFixed(0),
            }
          )}\n`;
          break;
        case "amount":
          temp += `${t(
            isPowerUp
              ? "weapons.upgrades.amount.powerUp"
              : "weapons.upgrades.amount.normal",
            {
              value: v,
            }
          )}\n`;
          break;
        case "interval":
          temp += `${t(
            isPowerUp
              ? "weapons.upgrades.interval.powerUp"
              : "weapons.upgrades.interval.normal",
            {
              value: (v * -0.001).toFixed(1),
            }
          )}\n`;
          break;
        case "duration":
          temp += `${t(
            isPowerUp
              ? "weapons.upgrades.duration.powerUp"
              : "weapons.upgrades.duration.normal",
            {
              value: isPowerUp ? (v * 100).toFixed(1) : (v * 0.001).toFixed(1),
            }
          )}\n`;
          break;
        case "speed":
          temp += `${t(
            isPowerUp
              ? "weapons.upgrades.speed.powerUp"
              : "weapons.upgrades.speed.normal",
            {
              value: (v * 100).toFixed(0),
            }
          )}\n`;
          break;
        case "cooldown":
          temp += `${t(
            isPowerUp
              ? "weapons.upgrades.cooldown.powerUp"
              : "weapons.upgrades.cooldown.normal",
            {
              value: (v * -100).toFixed(1),
            }
          )}\n`;
          break;
        case "magnet":
          temp += `${t("weapons.upgrades.magnet.normal", {
            value: (100 * v).toFixed(0),
          })}\n`;
          break;
        case "armor":
          temp += `${t("weapons.upgrades.armor.normal", {
            value: v,
          })}\n`;
          break;
        case "maxHp":
          temp += `${t("weapons.upgrades.maxHp.normal", {
            value: (v * 100).toFixed(1),
          })}\n`;
          break;
        case "growth":
          temp += `${t("weapons.upgrades.growth.normal", {
            value: (100 * v).toFixed(0),
          })}\n`;
          break;
        case "moveSpeed":
          temp += `${t("weapons.upgrades.moveSpeed.normal", {
            value: (100 * v).toFixed(0),
          })}\n`;
          break;
        case "luck":
          temp += `${t("weapons.upgrades.luck.normal", {
            value: (100 * v).toFixed(0),
          })}\n`;
          break;
        case "penetrating":
          temp += `${t(
            isPowerUp
              ? "weapons.upgrades.penetrating.powerUp"
              : "weapons.upgrades.penetrating.normal",
            {
              value: v.toFixed(0),
            }
          )}\n`;
          break;
      }
    }
    return temp.replace(/\n$/, "");
  }

  AssignItemData(pickupType: PickupType) {
    this.weaponType = pickupType;
    const itemData = PICKUPS[pickupType];

    // Use translation keys for name and description
    const nameKey =
      itemData.nameKey || `pickups.${String(pickupType).toLowerCase()}.name`;
    this.Name.setText(t(nameKey));

    const descriptionKey =
      itemData.descriptionKey ||
      `pickups.${String(pickupType).toLowerCase()}.description`;
    this.Description.setText(t(descriptionKey));

    this.NextLevel.setText("");

    if (!!itemData.iconName) {
      const atlasInfo = this.getAtlasTexture(itemData.iconName);
      if (atlasInfo) {
        this.Icon.setTexture(atlasInfo.key, atlasInfo.frame);
      } else {
        this.Icon.setTexture(itemData.iconName); // Fallback for non-spritesheet icons
      }
    } else {
      this.Icon.setFrame(itemData.frameName);
    }

    this.Icon.setDisplaySize(32 * this.scaleRatio, 32 * this.scaleRatio);
  }
}
